import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useViewContext } from "../../../contexts/ViewContext";

import { IconButton, TableCell, Tooltip } from "@mui/material";
import {
  AttachMoney,
  Check,
  Close,
  Description,
  Notifications,
  PlayArrow,
  EventNote,
  CheckCircle,
  EditCalendar,
} from "@mui/icons-material";
import { NextAppointmentsDialog } from "./NextAppointmentsDialog";
import { useClinicalRegisterContext } from "../../../contexts/ClinicalRegisterContext";
import { VerifyResponsibleDialog } from "../../../components/schedule/VerifyResponsibleDialog";
import ScheduleRescheduleDialog from "../../../components/schedule/ScheduleRescheduleDialog";
import { GetRelatedPerson } from "../../../services/fhir/relatedperson/GetRelatedPerson";
import { currentFeatures, oids } from "../../../configs/Settings";
import { useFhirData } from "../../../utils/useFhirData";
import { useLocationContext } from "../../../contexts/LocationContext";
import { useSessionContext } from "../../../contexts/SessionContext";
import { useOrganizationContext } from "../../../contexts/OrganizationContext";
import { useFormatter } from "../../../utils/useFormatter";
import { marketplaceService } from "../../../services/marketplace/MarketplaceService";
import { toast } from "react-toastify";
import { WarningAppointment } from "@/pages/schedule/components/WarningAppointment";
import {
  buildCobrancaConsultaNotification,
  buildCobrancaConsultaVetNotification,
} from "@/services/notification/notificationHelpers";
import { notificationService } from "@/services/notification/PostNotification";
import { EditAppointment } from "@/services/fhir";
import { patchAppointmentStatusAndTriggerPreConsultation } from "@/services/fhir/appointment/patchAppointmentStatusAndTriggerPreConsultation";
import {
  buildPreConsultationTriggerContextFromAppointment,
  buildScheduleManagerRequesterReference,
} from "@/services/crm/triggerClinicalAutomation";
import { PaymentPendingModal } from "../../../components/common/PaymentPendingModal";
import { AppointmentReminderModal } from "../../../components/common/AppointmentReminderModal";
import { resolveAppointmentReminderWhatsAppContext } from "../../../components/common/appointmentReminderWhatsApp";
import { GetPaymentNotice } from "@/services/fhir/paymentnotice/GetPaymentNotice";
import { PutPaymentNotice } from "@/services/fhir/paymentnotice/PutPaymentNotice";
import { GoogleCalendarButton } from "../../../components/common/GoogleCalendarButton";
import { usePostEvent } from "../../../services/google/PostEvent";
import { useDeleteEvent } from "../../../services/google/DeleteEvent";
import { GoogleCalendarSelectModal } from "../../../components/schedule/GoogleCalendarSelectModal";
import { useKeepPaymentModalOpenAfterCharge } from "@/hooks/useKeepPaymentModalOpenAfterCharge";

export function NextAppointmentsActions({ row }: any) {
  const { user, accessTokenGoogle } = useSessionContext();
  const navigate = useNavigate();
  const { verifyEncounter } = useClinicalRegisterContext();
  const { location } = useLocationContext();
  const { availableRoles } = useViewContext();
  const { organization } = useOrganizationContext();
  const [loading, setLoading] = useState(false);
  const [loadingPayment, setLoadingPayment] = useState<{
    [key: string]: boolean;
  }>({});
  const patientId = row?.reference?.participant?.split("-")?.[1];

  const [verifyDialog, setVerifyDialog] = useState<boolean>(false);
  const [related, setRelated] = useState<any>([]);

  // Estado para controlar a exibição do modal de aviso de pagamento
  const [showPaymentWarning, setShowPaymentWarning] = useState(false);

  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [reminderModalOpen, setReminderModalOpen] = useState(false);
  const [loadingSendCharge, setLoadingSendCharge] = useState(false);
  const [errorSendCharge, setErrorSendCharge] = useState<string | undefined>(
    undefined
  );
  const [loadingConfirmPayment, setLoadingConfirmPayment] = useState(false);

  const [payment, setPayment] = useState<any>([]);
  const [isPaid, setIsPaid] = useState(false);

  // Estados para o Google Calendar
  const [isInCalendar, setIsInCalendar] = useState<boolean>(false);
  const [loadingCalendar, setLoadingCalendar] = useState<boolean>(false);
  const { postEvent } = usePostEvent();
  const { deleteEvent } = useDeleteEvent();
  const keepOpenAfterCharge = useKeepPaymentModalOpenAfterCharge();

  // Estado para o modal de seleção do Google Calendar
  const [calendarModalOpen, setCalendarModalOpen] = useState(false);

  const fetchPayment = async () => {
    if (row?.appointment?.id) {
      const slotReference =
        row?.appointment?.slot?.[0]?.reference?.split("/")?.[1];
      const patientReference = row?.appointment?.participant
        ?.find((p: any) => p.actor?.reference?.includes("Patient"))
        ?.actor?.reference?.split("/")?.[1];

      if (slotReference && patientReference) {
        const filter = `status=active&_sort=-_lastUpdated&request=Slot/${slotReference}&response=Patient/${patientReference}&_count=1`;
        const response = await GetPaymentNotice({ filter });
        const entry = response?.data?.entry ?? [];
        setPayment(entry);
        // Pago = paymentStatus "cleared" (alinhado com ScheduleRescheduleDialog)
        const paid =
          entry?.[0]?.resource?.paymentStatus?.coding?.[0]?.code === "cleared";
        setIsPaid(!!paid);
        return entry;
      }
    }
    return [];
  };

  useEffect(() => {
    fetchPayment();
  }, [
    row?.appointment?.id,
    row?.appointment?.slot,
    row?.appointment?.participant,
  ]);

  /**
   * Indica se existe PaymentNotice retornado por GetPaymentNotice.
   * Sem PaymentNotice = consulta sem cobrança → ao confirmar deve ir para "booked".
   */
  const hasPaymentNotice = (payment ?? []).length > 0;

  // Verifica se o appointment está no Google Calendar
  useEffect(() => {
    if (row?.appointment) {
      const hasCalendarId = row?.appointment?.extension?.some(
        (ext: any) =>
          ext.url ===
            "https://fluxmed.com.br/fhir/StructureDefinition/googleCalendarId" &&
          ext.valueString
      );
      setIsInCalendar(!!hasCalendarId);
    }
  }, [row?.appointment]);

  async function fetchRelated() {
    const relatedResponse = await GetRelatedPerson({
      patient: `${oids.cpf}-${patientId}`,
    }).then((response) => response?.data?.entry);
    const verifyRelated = relatedResponse?.filter(
      (el: any) =>
        el?.resource?.identifier &&
        !el?.resource?.active &&
        el?.resource?.period?.start
    );
    if (verifyRelated?.length) setRelated(verifyRelated);
    else setRelated([]);
    return verifyRelated;
  }

  const [dialogCancelAppointment, setDialogCancelAppointment] =
    useState<boolean>(false);
  const [rescheduleDialogOpen, setRescheduleDialogOpen] =
    useState<boolean>(false);

  const locationId = useFhirData
    ?.findParticipant(row?.appointment?.participant, "Location")
    ?.reference?.split("/")?.[1];

  const practitioner = useFhirData?.findParticipant(
    row?.appointment?.participant,
    "Practitioner"
  )?.display;

  const healthcare = useFhirData?.findParticipant(
    row?.appointment?.participant,
    "HealthcareService"
  )?.display;

  const locationResource = location?.find((loc: any) =>
    loc?.resource?.id.includes(locationId)
  )?.resource;

  const address = locationResource?.address?.line?.join(", ");

  const auxVirtualService = useFhirData.virtualService(
    location?.find((loc: any) => loc?.resource?.id.includes(locationId))
      ?.resource
  );

  const link = row?.appointment?.virtualService?.[0]?.addressUrl;

  const reminderWhatsAppContext = useMemo(
    () =>
      resolveAppointmentReminderWhatsAppContext({
        appointment: {
          ...row?.appointment,
          patientName: row?.patient,
          patient: row?.patient,
          practitioner,
          healthcare,
          location: address,
          address,
          startTime: row?.appointment?.start,
        },
        locationResource,
        patientPhone: row?.phone,
      }),
    [
      row?.appointment,
      row?.patient,
      row?.phone,
      practitioner,
      healthcare,
      address,
      locationResource,
    ],
  );

  const startEncounter = row?.practitionerReference?.includes(user?.username);

  const appointmentStatus = row?.status ?? "";
  const isBooked = appointmentStatus === "booked";
  const isPending = appointmentStatus === "pending";
  const isProposed = appointmentStatus === "proposed";

  /** Ícones da barra — somente em booked (igual ScheduleQueueActions). */
  const showToolbarIconActions = isBooked;
  /** Cobrança — somente em pending. */
  const showPaymentButton = isPending;
  /** Lembrete — somente em booked. */
  const showReminderButton = showToolbarIconActions;
  const showStartService = isBooked;
  const showViewRecords = isBooked;
  const showRescheduleCancel =
    appointmentStatus !== "cancelled" && appointmentStatus !== "fulfilled";

  // Função para verificar se o atendimento foi pago
  const checkAppointmentPayment = () => {
    // Verificação baseada no status do atendimento
    // Se for 'pending' não foi pago, se for 'booked' foi pago
    return row?.status === "booked";
  };

  // Função para iniciar o atendimento
  const startAppointment = async () => {
    setLoading(true);
    await verifyEncounter(row?.appointment);
  };

  const handleOpenPaymentModal = () => {
    setPaymentModalOpen(true);
    setErrorSendCharge(undefined);
    setLoadingSendCharge(false);
    setLoadingConfirmPayment(false);
  };

  const handleSendCharge = async () => {
    setLoadingSendCharge(true);
    setErrorSendCharge(undefined);
    try {
      const slotReference = row?.appointment?.slot?.[0]?.reference || "";
      const patientName = row?.patient || "";
      const healthcareDisplay =
        row?.appointment?.participant.find((el: any) =>
          el?.actor?.reference?.includes("HealthcareService")
        )?.actor?.display || "";
      const practitionerName =
        row?.appointment?.participant.find((el: any) =>
          el?.actor?.reference?.includes("Practitioner")
        )?.actor?.display || "";
      const paymentLink = await marketplaceService.sendPaymentMessage({
        slotReference,
        patientName,
        healthcareDisplay,
        practitionerName,
        phoneNumber: row?.phone || "",
      });

      // Se não for defaultApp (modo vet), buscar informações do tutor
      if (!currentFeatures.defaultApp) {
        // Encontra o participant que é o paciente (pet)
        const patientParticipant = row?.appointment?.participant?.find(
          (p: any) => p?.actor?.reference?.startsWith("Patient/")
        );

        // Extrai o ID do paciente (pet)
        const patientId = patientParticipant?.actor?.reference?.split("/")?.[1];

        if (!patientId) {
          throw new Error("ID do paciente não encontrado");
        }

        // Busca o tutor relacionado ao pet
        const relatedPersonResponse = await GetRelatedPerson({
          patient: patientId,
          vetRelated: true, // Indica que é para sistema vet
        });

        // Extrai dados do tutor
        let tutorCpf = "";
        let tutorName = "";

        if (
          relatedPersonResponse &&
          relatedPersonResponse?.data?.entry?.length > 0
        ) {
          const relatedPerson = relatedPersonResponse.data.entry[0]?.resource;

          // Extrai CPF do tutor
          const tutorIdentifier = relatedPerson?.identifier?.[0]?.value || "";
          tutorCpf = tutorIdentifier;

          // Extrai nome do tutor
          const tutorNameObj = relatedPerson?.name?.find(
            (n: any) => n.use === "official" || n.use === "usual"
          );
          tutorName = tutorNameObj?.text || "";
        }

        if (!tutorCpf) {
          throw new Error("CPF do tutor não encontrado");
        }

        // Nome do pet (que está em patientName)
        const petName = patientName;

        // Nome da clínica/organização
        const hospitalName = organization?.name || "";

        // Envia notificação com template específico para vet
        // Usa 'as any' porque o tipo INotificationParameters não inclui 'pet' e 'hospital'
        // mas o template FluxvetPagamentoConsulta requer esses parâmetros
        await notificationService.sendNotification(
          buildCobrancaConsultaVetNotification({
            cpf: tutorCpf,
            nomeTutor: useFormatter.formatNameDisplay(tutorName) || "",
            nomePet: petName || "",
            nomeProfissional: practitionerName || "",
            nomeClinica: hospitalName || "",
            linkPagamento: paymentLink,
            organizationId: organization?.id,
          }),
          { organizationId: organization?.id },
        );
      } else {
        // Comportamento padrão para defaultApp
        await notificationService.sendNotification(
          buildCobrancaConsultaNotification({
            cpf: row?.reference?.participant?.split("-")?.[1] || "",
            nomePaciente: patientName,
            tipoConsulta: healthcareDisplay,
            nomeProfissional: practitionerName,
            linkPagamento: paymentLink,
            organizationId: organization?.id,
          }),
          { organizationId: organization?.id },
        );
      }

      // Atualizar appointment com extension notification-sent
      const notificationSentExtension = row?.appointment?.extension?.find(
        (ext: any) =>
          ext.url ===
          "https://fluxmed.com.br/fhir/StructureDefinition/notification-sent"
      );
      if (!notificationSentExtension?.valueBoolean) {
        await EditAppointment({
          ...row?.appointment,
          extension: [
            ...(row?.appointment?.extension || []),
            {
              url: "https://fluxmed.com.br/fhir/StructureDefinition/notification-sent",
              valueBoolean: true,
            },
          ],
        });
        if (!keepOpenAfterCharge && typeof row.setTabRefetch === "function") {
          row.setTabRefetch((prev: boolean) => !prev);
        }
      }
      setErrorSendCharge(undefined);
      toast.success("Cobrança enviada com sucesso!");
      if (!keepOpenAfterCharge) {
        setPaymentModalOpen(false);
      }
    } catch (error: any) {
      if (error?.response?.status === 404) {
        setErrorSendCharge(
          "Não é possível enviar cobrança para consulta em agenda sem custo. Confirme a consulta no botão a seguir"
        );
      } else {
        setErrorSendCharge(
          "Erro ao gerar link de pagamento. Por favor, tente novamente."
        );
      }
      throw error;
    } finally {
      setLoadingSendCharge(false);
    }
  };

  /**
   * Confirma consulta (modal de pagamento pendente).
   * Sem PaymentNotice: apenas muda status para "booked" (consulta sem cobrança).
   * Com PaymentNotice: marca como pago e atualiza o notice, depois booked.
   */
  const handleConfirmPayment = async () => {
    setLoadingConfirmPayment(true);
    try {
      await patchAppointmentStatusAndTriggerPreConsultation({
        appointmentId: row?.appointment?.id || "",
        status: "booked",
        preConsultationTrigger:
          buildPreConsultationTriggerContextFromAppointment({
            organizationId: organization?.id,
            appointment: row?.appointment,
            patientDisplayName: row?.patient,
            requesterReference: buildScheduleManagerRequesterReference({
              organizationId: organization?.id,
              practitionerUserId: user?.username,
            }),
          }) ?? undefined,
      });

      if (hasPaymentNotice && payment?.length > 0) {
        const paymentCopy = { ...payment[0]?.resource };
        paymentCopy.paymentStatus = {
          coding: [
            {
              system: "http://terminology.hl7.org/CodeSystem/paymentstatus",
              code: "paid",
            },
          ],
        };
        try {
          await PutPaymentNotice({
            paymentId: payment[0]?.resource?.id,
            data: paymentCopy,
          });
        } catch (err) {
          console.error("Erro ao atualizar PaymentNotice:", err);
        }
      }

      if (typeof row.setTabRefetch === "function") {
        row.setTabRefetch((prev: boolean) => !prev);
      }
      setPaymentModalOpen(false);
      toast.success("Consulta confirmada com sucesso!");
    } catch {
      toast.error("Erro ao confirmar consulta. Tente novamente.");
    } finally {
      setLoadingConfirmPayment(false);
    }
  };

  const handleClosePaymentModal = () => {
    setPaymentModalOpen(false);
    setErrorSendCharge(undefined);
    setLoadingSendCharge(false);
    setLoadingConfirmPayment(false);
  };

  // Função para adicionar ao Google Calendar
  const handleAddToCalendar = async () => {
    try {
      setLoadingCalendar(true);
      if (!accessTokenGoogle) {
        toast.warning("Token do Google Calendar não encontrado");
        return;
      }

      const eventData = {
        summary: `Consulta: ${healthcare}`,
        description: `Paciente: ${row?.patient}`,
        location: auxVirtualService
          ? row?.appointment?.virtualService?.[0]?.addressUrl || ""
          : address || "",
        start: {
          dateTime: row?.appointment?.start || new Date().toISOString(),
          timeZone: "America/Sao_Paulo",
        },
        end: {
          dateTime: row?.appointment?.end || new Date().toISOString(),
          timeZone: "America/Sao_Paulo",
        },
      };

      const eventResponse = await postEvent({
        eventData,
        accessTokenGoogle: accessTokenGoogle,
        refreshToken: user.refreshTokenGoogle || "",
      });

      if (eventResponse?.id) {
        let newExtensions: any[] = [];
        if (Array.isArray(row?.appointment?.extension)) {
          // Remove qualquer extensão antiga de googleCalendarId
          newExtensions = row.appointment.extension.filter(
            (ext: any) =>
              ext.url !==
              "https://fluxmed.com.br/fhir/StructureDefinition/googleCalendarId"
          );
        }
        // Adiciona a nova extensão no início
        newExtensions.unshift({
          url: "https://fluxmed.com.br/fhir/StructureDefinition/googleCalendarId",
          valueString: eventResponse.id,
        });
        await EditAppointment({
          ...row?.appointment,
          extension: newExtensions,
        });
        setIsInCalendar(true);
        if (typeof row.setTabRefetch === "function") {
          row.setTabRefetch((prev: boolean) => !prev);
        }
        toast.success("Adicionado ao Google Calendar");
      }
    } catch (error) {
      console.error("Erro ao adicionar ao Google Calendar:", error);
      toast.error("Erro ao adicionar ao Google Calendar");
    } finally {
      setLoadingCalendar(false);
    }
  };

  // Função para remover do Google Calendar
  const handleRemoveFromCalendar = async () => {
    try {
      setLoadingCalendar(true);
      const googleCalendarExtension = row?.appointment?.extension?.find(
        (ext: any) =>
          ext.url ===
          "https://fluxmed.com.br/fhir/StructureDefinition/googleCalendarId"
      );

      if (googleCalendarExtension?.valueString) {
        try {
          await deleteEvent({
            eventId: googleCalendarExtension.valueString,
            accessTokenGoogle: accessTokenGoogle || "",
            refreshToken: user?.refreshTokenGoogle || "",
          });
        } catch (deleteError) {
          console.error("Erro ao excluir evento:", deleteError);
        }
      }

      // Remove apenas a extensão googleCalendarId
      let newExtensions: any[] = [];
      if (Array.isArray(row?.appointment?.extension)) {
        newExtensions = row.appointment.extension.filter(
          (ext: any) =>
            ext.url !==
            "https://fluxmed.com.br/fhir/StructureDefinition/googleCalendarId"
        );
      }

      await EditAppointment({
        ...row?.appointment,
        extension: newExtensions,
      });

      setIsInCalendar(false);
      if (typeof row.setTabRefetch === "function") {
        row.setTabRefetch((prev: boolean) => !prev);
      }
      toast.success("Removido do Google Calendar");
    } catch (error) {
      console.error("Erro ao remover do Google Calendar:", error);
      toast.error("Erro ao remover do Google Calendar");
    } finally {
      setLoadingCalendar(false);
    }
  };

  return (
    <>
      {dialogCancelAppointment && (
        <NextAppointmentsDialog
          setModalOpen={setDialogCancelAppointment}
          open={dialogCancelAppointment}
          row={row}
          payment={payment}
          isPaid={isPaid}
        />
      )}

      {/* Mesmo uso do ScheduleQueueActions: Reagendar e "Confirmar e definir horário" (proposed) abrem este dialog */}
      {rescheduleDialogOpen && (
        <ScheduleRescheduleDialog
          isOpen={rescheduleDialogOpen}
          setIsOpen={setRescheduleDialogOpen}
          appointment={row?.appointment}
          setRefetch={row?.setTabRefetch}
          payment={payment}
          isPaid={isPaid}
        />
      )}
      <TableCell align="center">
        {row?.phone && showReminderButton && (
          <Tooltip title="Enviar lembrete de consulta">
            <IconButton
              color="secondary"
              disabled={loading}
              onClick={() => setReminderModalOpen(true)}
            >
              <Notifications />
            </IconButton>
          </Tooltip>
        )}

        {showRescheduleCancel && (
          <>
            <Tooltip title="Reagendar consulta">
              <IconButton
                sx={{
                  color: "warning.main",
                }}
                onClick={() => setRescheduleDialogOpen(true)}
              >
                <EditCalendar />
              </IconButton>
            </Tooltip>
            <Tooltip title="Cancelar consulta">
              <IconButton
                sx={{
                  color: "error600.main",
                  "&:hover": {
                    background: "error700.main",
                  },
                }}
                onClick={() => setDialogCancelAppointment(true)}
              >
                <Close />
              </IconButton>
            </Tooltip>
          </>
        )}

        {isProposed && (
          <Tooltip title="Confirmar e definir horário">
            <IconButton
              sx={{
                color: "success.main",
                "&:hover": {
                  backgroundColor: "success100.main",
                },
              }}
              onClick={() => setRescheduleDialogOpen(true)}
            >
              <CheckCircle />
            </IconButton>
          </Tooltip>
        )}

        {showPaymentButton && (
          <Tooltip title="Enviar cobrança">
            <IconButton
              sx={{ color: "primary800.main" }}
              onClick={handleOpenPaymentModal}
            >
              <AttachMoney />
              {row?.appointment?.extension?.find(
                (ext: any) =>
                  ext.url ===
                    "https://fluxmed.com.br/fhir/StructureDefinition/notification-sent" &&
                  ext.valueBoolean
              ) && <Check sx={{ fontSize: 16, color: "success700.main" }} />}
            </IconButton>
          </Tooltip>
        )}

        {showToolbarIconActions &&
          user?.refreshTokenGoogle &&
          row?.practitionerReference?.includes(user?.username) && (
            <GoogleCalendarButton
              isInCalendar={isInCalendar}
              onAddToCalendar={handleAddToCalendar}
              onRemoveFromCalendar={handleRemoveFromCalendar}
              disabled={loadingCalendar}
              loading={loadingCalendar}
            />
          )}

        {showToolbarIconActions &&
          availableRoles?.includes("practitioner") &&
          user?.refreshTokenGoogle &&
          row?.nextAppointments &&
          row?.practitionerReference?.includes(user?.username) && (
            <Tooltip title="Gerenciar múltiplos agendamentos no Google Calendar">
              <IconButton onClick={() => setCalendarModalOpen(true)}>
                <EventNote />
              </IconButton>
            </Tooltip>
          )}

        {showStartService && availableRoles?.includes("practitioner") && (
          <>
            {startEncounter && (
              <Tooltip title="Iniciar atendimento">
                <IconButton
                  disabled={loading}
                  onClick={async () => {
                    const verifyRelated = await fetchRelated();

                    if (verifyRelated?.length) {
                      setVerifyDialog(true);
                    } else {
                      const isPaid = checkAppointmentPayment();

                      if (!isPaid) {
                        setShowPaymentWarning(true);
                      } else {
                        startAppointment();
                      }
                    }
                  }}
                >
                  <PlayArrow />
                </IconButton>
              </Tooltip>
            )}
            {showViewRecords && (
              <Tooltip title="Acessar registros">
                <IconButton
                  onClick={() => {
                    if (patientId)
                      navigate("/summary", {
                        state: {
                          patientId,
                          appointment: row?.appointment,
                        },
                      });
                  }}
                >
                  <Description />
                </IconButton>
              </Tooltip>
            )}
          </>
        )}
      </TableCell>

      {verifyDialog && (
        <VerifyResponsibleDialog
          handleClose={() => setVerifyDialog(false)}
          isOpen={true}
          loading={loading}
          setLoading={setLoading}
          dataAppointment={row?.appointment}
          related={related}
          fetchRelated={fetchRelated}
          patientId={patientId}
        />
      )}

      {/* Componente de aviso de pagamento */}
      <WarningAppointment
        isOpen={showPaymentWarning}
        handleClose={() => setShowPaymentWarning(false)}
        onConfirm={() => {
          setShowPaymentWarning(false);
          startAppointment();
        }}
        appointmentDescription="Esta consulta"
      />

      {reminderModalOpen && (
        <AppointmentReminderModal
          isOpen={reminderModalOpen}
          onClose={() => setReminderModalOpen(false)}
          reminderContext={reminderWhatsAppContext}
        />
      )}

      {paymentModalOpen && (
        <PaymentPendingModal
          isOpen={paymentModalOpen}
          onClose={handleClosePaymentModal}
          loadingSendCharge={loadingSendCharge}
          errorSendCharge={errorSendCharge}
          loadingConfirmPayment={loadingConfirmPayment}
          onSendCharge={handleSendCharge}
          onConfirmPayment={handleConfirmPayment}
          appointment={row?.appointment}
          notificationSent={
            !!row?.appointment?.extension?.find(
              (ext: any) =>
                ext.url ===
                  "https://fluxmed.com.br/fhir/StructureDefinition/notification-sent" &&
                ext.valueBoolean
            )
          }
          whatsappNoticeContext={{
            practitionerName: practitioner,
            appointmentStart: row?.appointment?.start,
            locationLabel: auxVirtualService
              ? "Teleconsulta"
              : address ||
                useFhirData?.findParticipant(
                  row?.appointment?.participant,
                  "Location",
                )?.display ||
                "local não informado",
            patientName: row?.patient,
            patientPhone: row?.phone,
          }}
        />
      )}

      {/* Modal para gerenciar múltiplos agendamentos no Google Calendar */}
      {calendarModalOpen && row?.nextAppointments && (
        <GoogleCalendarSelectModal
          open={calendarModalOpen}
          onClose={() => setCalendarModalOpen(false)}
          scheduleId={row?.schedule?.resource?.id}
          appointments={row?.nextAppointments}
          nextAppointmentsPatients={row?.nextAppointmentsPatients}
          onSuccess={() => {
            setCalendarModalOpen(false);
            if (typeof row.setTabRefetch === "function") {
              row.setTabRefetch((prev: boolean) => !prev);
            }
          }}
        />
      )}
    </>
  );
}
